import { useInvalidatingMutation } from '@/api/hooks';
import type { CodeString } from '@/consts/codes';
import { settingsMutationEndpoints } from '@/modules/MD/api/settings/mutations';
import { SettingCategory, settingsQueries } from '@/modules/MD/api/settings/queries';
import { useQuery } from '@tanstack/react-query';

export const useFavorites = () => {
  return useQuery(settingsQueries.list(SettingCategory.Favorites));
};

export const useAddFavorite = () => {
  return useInvalidatingMutation(
    (code: CodeString) => settingsMutationEndpoints.add(SettingCategory.Favorites, code),
    settingsQueries.all(SettingCategory.Favorites),
    'error.favorites.add',
    'success.favorites.add'
  );
};

export const useRemoveFavorite = () => {
  return useInvalidatingMutation(
    (code: CodeString) => settingsMutationEndpoints.remove(SettingCategory.Favorites, code),
    settingsQueries.all(SettingCategory.Favorites),
    'error.favorites.remove',
    'success.favorites.remove'
  );
};

export const useToggleFavorite = () => {
  const add = useAddFavorite();
  const remove = useRemoveFavorite();

  return {
    mutate: async ({ code, isFavorite }: { code: CodeString; isFavorite: boolean }) => {
      if (isFavorite) return remove.mutateAsync(code);
      return add.mutateAsync(code);
    },
    isPending: add.isPending || remove.isPending
  };
};

export const useRecents = () => {
  return useQuery(settingsQueries.list(SettingCategory.Recents));
};

export const useAddRecent = () => {
  return useInvalidatingMutation(
    (code: CodeString) => settingsMutationEndpoints.add(SettingCategory.Recents, code),
    settingsQueries.all(SettingCategory.Recents),
    'error.recents.add'
  );
};
